/**
 * Centralized Time Utilities
 * 
 * CRITICAL: All times are stored and compared in UTC to avoid timezone bugs.
 * 
 * Rules:
 * 1. Database DateTime fields are stored in UTC (Prisma handles this automatically)
 * 2. All comparisons use UTC timestamps
 * 3. Date strings (YYYY-MM-DD) represent calendar dates, not specific moments
 * 4. Time strings (HH:MM) represent local time in the user's timezone
 * 5. When combining date + time, we convert to UTC using the user's timezone offset
 * 
 * NEVER:
 * - Use `new Date(year, month, day)` without timezone awareness
 * - Compare dates without converting to UTC first
 * - Assume server timezone matches user timezone
 * - Mix local time and UTC in comparisons
 */

/**
 * Get current UTC timestamp in milliseconds
 * Use this for all "now" comparisons
 */
export function getNowUTC(): number {
  return Date.now() // JavaScript Date.now() returns UTC milliseconds
}

/**
 * Get current UTC Date object
 */
export function getNowUTCDate(): Date {
  return new Date() // JavaScript Date objects are always in UTC internally
}

/**
 * Get today's date at midnight UTC
 * Use for date-only comparisons
 */
export function getTodayUTC(): Date {
  const now = getNowUTCDate()
  const today = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    0, 0, 0, 0
  ))
  return today
}

/**
 * Parse a date string (YYYY-MM-DD) and return UTC timestamp for midnight in that date
 * The date string represents a calendar date, not a specific moment
 * 
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns UTC Date object representing midnight UTC for that date
 */
export function parseDateStringToUTC(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  // Create UTC date at midnight - this represents the calendar date
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
}

/**
 * Convert a local date + time to UTC timestamp
 *
 * @param dateStr - Date string in YYYY-MM-DD format (local calendar date)
 * @param timeStr - Time string in HH:MM format (local time)
 * @param timezoneOffsetMinutes - Signed UTC offset in minutes, using the
 *                                 `-getTimezoneOffset()` convention every caller
 *                                 in this codebase uses:
 *                                   Negative for timezones behind UTC (e.g., EST = -300)
 *                                   Positive for timezones ahead of UTC (e.g., JST = +540)
 * @returns UTC Date object
 *
 * Example:
 *   dateStr = "2024-01-15", timeStr = "09:00", offset = -300 (EST, UTC-5)
 *   Local: 2024-01-15 09:00:00 EST
 *   UTC: 2024-01-15 14:00:00 UTC (09:00 + 5 hours)
 */
export function localDateTimeToUTC(
  dateStr: string,
  timeStr: string,
  timezoneOffsetMinutes: number
): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  const [hours, minutes] = timeStr.split(':').map(Number)

  // Construct a UTC timestamp as if the local components were UTC.
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0))

  // To go from local-as-UTC back to true UTC, subtract the signed offset.
  // EST offset = -300 → subtract -300 = add 5 hours, so 09:00 EST → 14:00 UTC.
  return new Date(utcDate.getTime() - (timezoneOffsetMinutes * 60 * 1000))
}

/**
 * Convert a UTC date to a local date string (YYYY-MM-DD)
 * Uses the provided timezone offset to determine the local calendar date
 * 
 * @param utcDate - UTC Date object
 * @param timezoneOffsetMinutes - Timezone offset in minutes
 * @returns Date string in YYYY-MM-DD format
 */
export function utcDateToLocalDateString(
  utcDate: Date,
  timezoneOffsetMinutes: number
): string {
  // Adjust UTC date by timezone offset to get local date
  const localDate = new Date(utcDate.getTime() - (timezoneOffsetMinutes * 60 * 1000))
  const year = localDate.getUTCFullYear()
  const month = String(localDate.getUTCMonth() + 1).padStart(2, '0')
  const day = String(localDate.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a UTC Date object to ISO string
 * Use for API responses and database storage
 */
export function formatUTCToISO(utcDate: Date): string {
  return utcDate.toISOString()
}

/**
 * Format a UTC Date object to local date string (YYYY-MM-DD)
 * For display purposes only - uses browser's timezone
 */
export function formatUTCToLocalDateString(utcDate: Date): string {
  const year = utcDate.getFullYear()
  const month = String(utcDate.getMonth() + 1).padStart(2, '0')
  const day = String(utcDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a UTC Date object to local time string (HH:MM)
 * For display purposes only - uses browser's timezone
 */
export function formatUTCToLocalTimeString(utcDate: Date): string {
  const hours = String(utcDate.getHours()).padStart(2, '0')
  const minutes = String(utcDate.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Check if a UTC date is today (same calendar day in UTC)
 */
export function isTodayUTC(utcDate: Date): boolean {
  const today = getTodayUTC()
  const dateMidnight = new Date(Date.UTC(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    0, 0, 0, 0
  ))
  return dateMidnight.getTime() === today.getTime()
}

/**
 * Check if a UTC date is in the past (before today at midnight UTC)
 */
export function isPastDateUTC(utcDate: Date): boolean {
  const today = getTodayUTC()
  const dateMidnight = new Date(Date.UTC(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    0, 0, 0, 0
  ))
  return dateMidnight.getTime() < today.getTime()
}

/**
 * Check if a UTC datetime is in the past
 */
export function isPastDateTimeUTC(utcDateTime: Date): boolean {
  return utcDateTime.getTime() < getNowUTC()
}

/**
 * Parse a Prisma DateTime field (which is already UTC) to a Date object
 * Handles both Date objects and ISO strings
 */
export function parsePrismaDateTime(dateTime: Date | string | null): Date | null {
  if (!dateTime) return null
  if (dateTime instanceof Date) return dateTime
  return new Date(dateTime) // ISO string, already in UTC
}

/**
 * Extract date components from a UTC Date object
 * Returns components in UTC (not local timezone)
 */
export function getUTCDateComponents(utcDate: Date): {
  year: number
  month: number // 0-11
  day: number
  hours: number
  minutes: number
} {
  return {
    year: utcDate.getUTCFullYear(),
    month: utcDate.getUTCMonth(),
    day: utcDate.getUTCDate(),
    hours: utcDate.getUTCHours(),
    minutes: utcDate.getUTCMinutes()
  }
}

/**
 * Create a UTC Date from date components
 * All components are interpreted as UTC
 */
export function createUTCDate(
  year: number,
  month: number, // 0-11
  day: number,
  hours: number = 0,
  minutes: number = 0,
  seconds: number = 0
): Date {
  return new Date(Date.UTC(year, month, day, hours, minutes, seconds, 0))
}

/**
 * Parse a stored "calendar date" — either a YYYY-MM-DD string or a UTC-midnight
 * DateTime (the form we use when a date represents a calendar day, not an instant)
 * — into a local Date with the same y/m/d components.
 *
 * Use this when rendering weekday/month/day from `Order.pickupDate`,
 * `DriverRoute.routeDate`, etc. Avoids the off-by-one weekday bug where
 * `new Date("2026-04-26")` parses to UTC midnight and then displays as the
 * previous day in negative-UTC timezones.
 */
export function parseCalendarDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null
  if (typeof input === 'string') {
    const m = input.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) {
      const d = new Date(input)
      if (isNaN(d.getTime())) return null
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    }
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10))
  }
  return new Date(input.getUTCFullYear(), input.getUTCMonth(), input.getUTCDate())
}

/**
 * Get browser timezone offset in minutes, in the `-getTimezoneOffset()` convention.
 * Negative for timezones behind UTC (e.g., EST = -300)
 * Positive for timezones ahead of UTC (e.g., JST = +540)
 *
 * This is the OPPOSITE sign of JavaScript's getTimezoneOffset(), which returns
 * positive for timezones behind UTC. This value is what localDateTimeToUTC expects.
 */
export function getBrowserTimezoneOffset(): number {
  return -new Date().getTimezoneOffset()
}

// ---------------------------------------------------------------------------
// Central-time (America/Chicago) helpers
//
// Product rule: every wall-clock time in Deeliver is Central Time, and all
// display is pinned to Central regardless of the viewer's browser or the
// server's zone. These helpers use Intl.DateTimeFormat with an explicit
// timeZone so output is identical on a UTC server, a Tokyo browser, and a
// Texas browser. No hand-rolled DST rules. Keep this file free of server-only
// imports — client components import it too.
// ---------------------------------------------------------------------------

export const APP_TIME_ZONE = 'America/Chicago'
export const APP_TIME_ZONE_LABEL = 'CT'

/**
 * Signed UTC offset of America/Chicago at `at` (default now), in the
 * `-getTimezoneOffset()` convention used by localDateTimeToUTC: CDT = -300, CST = -360.
 * Computed via Intl.DateTimeFormat().formatToParts — no hand-rolled DST rules.
 */
export function getCentralOffsetMinutes(at: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at)

  const map: Record<string, string> = {}
  for (const p of parts) map[p.type] = p.value

  // Treat the Central wall-clock components as if they were UTC, then compare
  // to the true instant. offset = wallClockAsUTC - actualUTC.
  const wallClockAsUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  )
  return Math.round((wallClockAsUTC - at.getTime()) / 60000)
}

/**
 * Today's calendar date in Central, "YYYY-MM-DD".
 */
export function getCentralTodayString(at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)
  const map: Record<string, string> = {}
  for (const p of parts) map[p.type] = p.value
  return `${map.year}-${map.month}-${map.day}`
}

/**
 * Normalize "HH:MM", "H:MM", "h:mm am", "h:mm PM", "hh:mm:ss" to 24h "HH:MM";
 * null if unparseable.
 */
export function normalizeTimeTo24h(timeStr: string | null | undefined): string | null {
  if (timeStr == null) return null
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i)
  if (!m) return null

  let hours = parseInt(m[1], 10)
  const minutes = parseInt(m[2], 10)
  if (minutes > 59) return null

  const ampm = m[4] ? m[4].toLowerCase().replace(/\./g, '') : null
  if (ampm) {
    if (hours < 1 || hours > 12) return null
    if (ampm === 'pm' && hours !== 12) hours += 12
    if (ampm === 'am' && hours === 12) hours = 0
  } else if (hours > 23) {
    return null
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Interpret dateStr ("YYYY-MM-DD") + timeStr (any form normalizeTimeTo24h accepts)
 * as Central wall-clock and return the UTC instant. DST-aware: compute with the
 * offset at the naive instant, then re-check the offset at the result and adjust
 * once if they differ. Throws on unparseable input.
 */
export function centralDateTimeToUTC(dateStr: string, timeStr: string): Date {
  const dm = (dateStr ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!dm) throw new Error(`centralDateTimeToUTC: unparseable date "${dateStr}"`)
  const norm = normalizeTimeTo24h(timeStr)
  if (!norm) throw new Error(`centralDateTimeToUTC: unparseable time "${timeStr}"`)

  const year = Number(dm[1])
  const month = Number(dm[2])
  const day = Number(dm[3])
  const [hours, minutes] = norm.split(':').map(Number)

  // Naive instant: Central wall-clock components treated as UTC.
  const naive = Date.UTC(year, month - 1, day, hours, minutes, 0, 0)

  // actualUTC = naive - offset (offset = wallClock - actualUTC).
  const offset1 = getCentralOffsetMinutes(new Date(naive))
  const utc1 = naive - offset1 * 60000

  // Re-check the offset at the computed instant; adjust once across a DST edge.
  const offset2 = getCentralOffsetMinutes(new Date(utc1))
  if (offset2 === offset1) {
    return new Date(utc1)
  }

  const utc2 = naive - offset2 * 60000

  // Spring-forward gap: if the requested wall-clock time does not exist in
  // Central (the offset-corrected instant formats back to a different HH:MM),
  // keep the first-pass result so the non-existent time shifts forward — e.g.
  // 02:30 on a spring-forward day becomes 03:30 CDT, not 01:30 CST. Otherwise
  // use the offset-corrected instant (normal cross-transition case).
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(utc2))
  const wallMap: Record<string, string> = {}
  for (const p of parts) wallMap[p.type] = p.value
  const wallClock = `${wallMap.hour}:${wallMap.minute}`

  return new Date(wallClock === norm ? utc2 : utc1)
}

export type DateStyle = 'short' | 'medium' | 'long' | 'numeric'
// short   => "Sat, Apr 25"
// medium  => "Apr 25, 2026"
// long    => "Saturday, April 25, 2026"
// numeric => "4/25/2026"

function dateStyleOptions(style: DateStyle): Intl.DateTimeFormatOptions {
  switch (style) {
    case 'short':
      return { weekday: 'short', month: 'short', day: 'numeric' }
    case 'long':
      return { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }
    case 'numeric':
      return { month: 'numeric', day: 'numeric', year: 'numeric' }
    case 'medium':
    default:
      return { month: 'short', day: 'numeric', year: 'numeric' }
  }
}

/** Coerce an instant input (ISO string, epoch ms, or Date) to a valid Date, or null. */
function toInstant(input: string | number | Date | null | undefined): Date | null {
  if (input == null || input === '') return null
  const d = input instanceof Date ? input : new Date(input)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Format a CALENDAR date without ever shifting the day. Accepts "YYYY-MM-DD", an
 * ISO string whose first 10 chars are the date, or a Date at UTC midnight (uses
 * UTC getters). Returns 'N/A' for null/undefined/invalid.
 */
export function formatCalendarDate(
  input: string | Date | null | undefined,
  style: DateStyle = 'medium'
): string {
  if (input == null) return 'N/A'

  let year: number
  let month: number
  let day: number

  if (typeof input === 'string') {
    const m = input.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return 'N/A'
    year = Number(m[1])
    month = Number(m[2])
    day = Number(m[3])
  } else if (input instanceof Date) {
    if (isNaN(input.getTime())) return 'N/A'
    year = input.getUTCFullYear()
    month = input.getUTCMonth() + 1
    day = input.getUTCDate()
  } else {
    return 'N/A'
  }

  const d = new Date(Date.UTC(year, month - 1, day))
  return new Intl.DateTimeFormat('en-US', {
    ...dateStyleOptions(style),
    timeZone: 'UTC',
  }).format(d)
}

/**
 * Format an INSTANT (ISO string, epoch ms, or Date) as a date in America/Chicago.
 * 'N/A' for null/invalid.
 */
export function formatInstantDate(
  input: string | number | Date | null | undefined,
  style: DateStyle = 'medium'
): string {
  const d = toInstant(input)
  if (!d) return 'N/A'
  return new Intl.DateTimeFormat('en-US', {
    ...dateStyleOptions(style),
    timeZone: APP_TIME_ZONE,
  }).format(d)
}

/**
 * "2:05 PM"; with seconds "2:05:33 PM"; with withZone "2:05 PM CDT".
 */
export function formatInstantTime(
  input: string | number | Date | null | undefined,
  opts: { seconds?: boolean; withZone?: boolean } = {}
): string {
  const d = toInstant(input)
  if (!d) return 'N/A'
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: APP_TIME_ZONE,
  }
  if (opts.seconds) options.second = '2-digit'
  if (opts.withZone) options.timeZoneName = 'short'
  return new Intl.DateTimeFormat('en-US', options).format(d)
}

/**
 * "Apr 25, 2026, 2:05 PM"; withZone appends "CDT"/"CST".
 */
export function formatInstantDateTime(
  input: string | number | Date | null | undefined,
  opts: { style?: DateStyle; withZone?: boolean } = {}
): string {
  const d = toInstant(input)
  if (!d) return 'N/A'
  const options: Intl.DateTimeFormatOptions = {
    ...dateStyleOptions(opts.style ?? 'medium'),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: APP_TIME_ZONE,
  }
  if (opts.withZone) options.timeZoneName = 'short'
  return new Intl.DateTimeFormat('en-US', options).format(d)
}

/**
 * Format a wall-clock time STRING ("HH:MM" or "h:mm am") as "h:mm AM".
 * Returns the input unchanged if unparseable, 'N/A' for null/undefined.
 */
export function formatTimeString(timeStr: string | null | undefined): string {
  if (timeStr == null) return 'N/A'
  const norm = normalizeTimeTo24h(timeStr)
  if (!norm) return timeStr
  const [h, m] = norm.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

/**
 * Convenience: "9:00 AM - 10:00 AM" from two time strings.
 */
export function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  return `${formatTimeString(start)} - ${formatTimeString(end)}`
}
